import React from 'react';

import { Cabinet, Marquee, Window, Reels, Reel, Strip, Cell, Glyph, Seven, Payline } from './SlotMachine.styles';

/**
 * The "777" easter-egg slot machine rendered in the Lumio thread when the jackpot
 * code toggles the `show_in_progress` flag (see `useLumioChat.send`).
 *
 * Three reels spin and stop left → right (CSS keyframes; the stagger comes from
 * each reel's animation duration). `result` decides the row they land on:
 *  - `win`  → 7 · 7 · 7 (flag just turned ON) with a gold ring + glow.
 *  - `lose` → three different fruit (flag just turned OFF), re-rolled each time,
 *    with a small shudder.
 *
 * The result symbol is the LAST cell of every strip, so the reels' resting state
 * (and the `prefers-reduced-motion` fallback) shows the outcome with no animation.
 * Purely decorative (the accessible outcome is the caption rendered beneath it), so
 * the machine is `aria-hidden`.
 */

/** The reel symbols. `SEVEN` renders as the styled crimson "7"; the rest are emoji. */
const POOL = ['🍒', '🍋', '🔔', '⭐', '💎', 'SEVEN'] as const;
type Sym = (typeof POOL)[number];

/** The non-seven symbols a losing row is drawn from. */
const FRUIT = POOL.filter((s) => s !== 'SEVEN');

/** Jackpot row. */
const WIN_ROW: [Sym, Sym, Sym] = ['SEVEN', 'SEVEN', 'SEVEN'];

/** A losing row: three DISTINCT fruit, shuffled fresh each time (never a 7, never a
 * pair, so it always reads as a clear miss and differs run to run). */
const pickLoseRow = (): [Sym, Sym, Sym] => {
  const pool = [...FRUIT];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return [pool[0], pool[1], pool[2]];
};

const SEVEN_GLYPH = '7';
const STRIP_LEN = 22;

/** A reel's full symbol strip: pseudo-varied filler (so the three reels don't spin
 * in lockstep) ending on the landing symbol. Only the filler is deterministic; the
 * landing row is supplied by the caller (random for a loss). */
const buildStrip = (reelIndex: number, result: Sym): Sym[] => {
  const cells: Sym[] = [];
  for (let i = 0; i < STRIP_LEN - 1; i += 1) {
    cells.push(POOL[(i * 5 + reelIndex * 3 + 1) % POOL.length]);
  }
  cells.push(result);
  return cells;
};

export const SlotMachine = ({ result }: { result: 'win' | 'lose' }): React.ReactElement => {
  // The landing row is fixed once per mount (a fresh random trio of fruit on a
  // loss), so it stays put across re-renders; only a NEW "777" card re-rolls it.
  const [row] = React.useState<[Sym, Sym, Sym]>(() => (result === 'win' ? WIN_ROW : pickLoseRow()));

  return (
    <Cabinet $result={result} aria-hidden="true">
      <Marquee />
      <Window $result={result}>
        <Reels>
          {row.map((target, reelIndex) => (
            <Reel key={reelIndex}>
              <Strip>
                {buildStrip(reelIndex, target).map((sym, i) => (
                  <Cell key={i}>{sym === 'SEVEN' ? <Seven>{SEVEN_GLYPH}</Seven> : <Glyph>{sym}</Glyph>}</Cell>
                ))}
              </Strip>
            </Reel>
          ))}
        </Reels>
        <Payline />
      </Window>
    </Cabinet>
  );
};

export default SlotMachine;
